import { useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

/**
 * Phase 190 — number of OPEN flagged payments for the sidebar badge.
 *   useOpenFlagCount(code, enabled)  → tenant (Municipal Admin: whole municipality; Supervisor: own territory — the API scopes it)
 *   useSuperOpenFlagCount()          → every live municipality (Super Admin)
 * Refreshes every minute and immediately after a review / comment / correction (`notifyFlagsChanged()`).
 */
export const FLAGS_CHANGED_EVENT = "flagged-payments:changed";
export const notifyFlagsChanged = () => { try { window.dispatchEvent(new Event(FLAGS_CHANGED_EVENT)); } catch (e) { /* ignore */ } };

const REFRESH_MS = 60_000;

function usePolledCount(fetcher, deps, enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return undefined; }
    let alive = true;
    const load = () => fetcher().then((n) => { if (alive) setCount(n || 0); }).catch(() => { /* badge is best-effort */ });
    load();
    const t = setInterval(load, REFRESH_MS);
    window.addEventListener(FLAGS_CHANGED_EVENT, load);
    return () => { alive = false; clearInterval(t); window.removeEventListener(FLAGS_CHANGED_EVENT, load); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return count;
}

export function useOpenFlagCount(code, enabled = true) {
  const uid = useAuth()?.auth?.user?.id || null;
  return usePolledCount(() => api.get(`/t/${code}/flagged-payments/summary`).then((r) => r.data?.open?.count || 0), [code, uid, enabled], !!code && enabled);
}

export function useSuperOpenFlagCount(enabled = true) {
  const uid = useAuth()?.auth?.user?.id || null;
  return usePolledCount(() => api.get("/platform/flagged-payments/summary").then((r) => r.data?.open || 0), [uid, enabled], enabled);
}
